import { validationPopup } from "../redux/actions";
import { addItem, deleteItem, deleteUser } from "../services/AdminService";
import countries from "../data/countries.json";

// Helper function to get country name from nationality code
export const getCountryName = (nationalityCode) => {
  const country = countries.find(
    (country) => country.value === nationalityCode
  );
  return country ? country.label : nationalityCode;
};

// Filter functions
export const getFilteredUsers = (users, searchTerm, roleFilter) => {
  return users.filter((user) => {
    const matchesRole =
      roleFilter === "ALL" ||
      (user.sub_role || "User").toLowerCase() === roleFilter.toLowerCase();

    if (!searchTerm) return matchesRole;

    // Get country name for the user's nationality
    const countryName = getCountryName(user.nationality);

    // Search in all relevant fields
    const searchFields = [
      user.username,
      user.fullName,
      user.sub_role,
      user.nationality,
      countryName, // Add country name to searchable fields
      user.intro,
      String(user.clicks),
      String(user.favs),
      user.uxer?.position,
      ...(user.uxer?.skills || []),
      ...(user.uxer?.spokenLang || []),
    ].map((field) => (field || "").toString().toLowerCase());

    return (
      matchesRole && searchFields.some((field) => field.includes(searchTerm))
    );
  });
};

export const getFilteredItems = (items, searchTerm, categoryFilter) => {
  console.log(categoryFilter);
  return items.filter((item) => {
    const matchesCategory =
      categoryFilter === "ALL" ||
      categoryFilter.includes(item.category_id[0]?.name.toUpperCase());

    if (!searchTerm) return matchesCategory;

    // Search in all relevant fields
    const searchFields = [
      item.name,
      item.link,
      ...(item.description || []),
      ...(item.tags || []),
      ...(item.filterTag || []),
      ...(item.platform || []),
      item.price,
      item.category_id[0]?.name,
      String(item.likes),
      String(item.clicks),
    ].map((field) => (field || "").toString().toLowerCase());

    return (
      matchesCategory &&
      searchFields.some((field) => field.includes(searchTerm))
    );
  });
};

// Action handlers
export const handleDeleteUser = async (user, users, setUsers, dispatch) => {
  if (!user || !user._id) return;

  try {
    dispatch(
      validationPopup({
        message: "Deleting user...",
        icon: "loading_ani",
      })
    );

    await deleteUser(user._id);

    // Update users list after successful deletion
    const updatedUsers = users.filter((u) => u._id !== user._id);
    setUsers(updatedUsers);

    dispatch(
      validationPopup({
        message: "User deleted successfully",
        icon: "like",
        type: "success",
      })
    );

    return true;
  } catch (error) {
    console.error("Error deleting user:", error);
    dispatch(
      validationPopup({
        message: "Failed to delete user. Please try again.",
        icon: "horror",
        type: "error",
      })
    );

    return false;
  }
};

export const handleDeleteItem = async (item, items, setItems, dispatch) => {
  if (!item || !item._id) return;

  try {
    dispatch(
      validationPopup({
        message: "Deleting item...",
        icon: "loading_ani",
      })
    );

    await deleteItem(item._id);

    // Update items list after successful deletion
    const updatedItems = items.filter((i) => i._id !== item._id);
    setItems(updatedItems);

    dispatch(
      validationPopup({
        message: "Item deleted successfully",
        icon: "like",
        type: "success",
      })
    );

    return true;
  } catch (error) {
    console.error("Error deleting item:", error);
    dispatch(
      validationPopup({
        message: "Failed to delete item. Please try again.",
        icon: "horror",
        type: "error",
      })
    );

    return false;
  }
};

export const handleAddItem = async (itemData, image, dispatch) => {
  try {
    dispatch(
      validationPopup({
        message: "Adding new item...",
        icon: "loading_ani",
      })
    );

    const response = await addItem(itemData, image);

    dispatch(
      validationPopup({
        message: "Item added successfully!",
        icon: "like",
        type: "success",
      })
    );

    return response;
  } catch (error) {
    console.error("Error adding item:", error);
    dispatch(
      validationPopup({
        message: "Failed to add item. Please try again.",
        icon: "horror",
        type: "error",
      })
    );
    throw error;
  }
};

export const categoryIcon = (category) => {
  if (category === "tools") {
    return "tool";
  } else if (category === "resos") {
    return "resos";
  } else if (category === "ideaz") {
    return "idea";
  } else {
    return "apps";
  }
};

// Add this validation function
export const validateItemForm = (formData, image, isNewItem = true) => {
  const errors = {};

  // Required fields for all categories
  if (!formData.name?.trim()) {
    errors.name = "Name is required";
  }

  if (!formData.engDescription?.trim()) {
    errors.engDescription = "English description is required";
  }

  if (!formData.jpDescription?.trim()) {
    errors.jpDescription = "Japanese description is required";
  }

  if (!formData.link?.trim()) {
    errors.link = "Link is required";
  } else if (!isValidUrl(formData.link)) {
    errors.link = "Please enter a valid URL";
  }

  // Image validation for new items
  if (isNewItem && !image) {
    errors.image = "Image is required";
  }

  // Category-specific validations
  if (formData.category === "tools") {
    // Platform validation for tools
    if (!formData.platform || formData.platform.length === 0) {
      errors.platform = "At least one platform must be selected";
    }

    // Validate platform-specific links if platforms are selected
    if (
      formData.platform.includes("android") &&
      !formData.linkAndroid?.trim()
    ) {
      errors.linkAndroid =
        "Android link is required when Android platform is selected";
    }
    if (
      formData.platform.includes("windows") &&
      !formData.linkWindows?.trim()
    ) {
      errors.linkWindows =
        "Windows link is required when Windows platform is selected";
    }
  }

  // Tags validation
  if (!formData.tags?.trim()) {
    errors.tags = "At least one tag is required";
  }

  // Filter tag validation
  if (!formData.filterTag || formData.filterTag.length === 0) {
    errors.filterTag = "At least one filter tag must be selected";
  }

  return errors;
};

// Helper function to validate URLs
const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};
